import type { ServiceCategoryRow } from '@/api/booking/getServiceCategoriesQuery';
import type { Service } from '@/types/booking';

import { getVisibleL1Categories } from './getVisibleL1Categories';

export interface ServiceListGroup {
  id: string;
  name: string;
}

export interface ServiceGroup {
  group: ServiceListGroup;
  services: Service[];
}

interface GetServiceGroupsParams {
  services: Service[];
  rows: ServiceCategoryRow[];
}

/**
 * One group per visible L2 under a visible L1 that holds at least one of
 * `services`, ordered by L1 `position`, L2 `position`, then id. A service in
 * several L2s is listed in each; one in no visible L2 is in no group (see
 * `getUngroupedServices`).
 */
export function getServiceGroups({
  services,
  rows,
}: GetServiceGroupsParams): ServiceGroup[] {
  const groups: ServiceGroup[] = [];

  for (const l1 of getVisibleL1Categories({ rows })) {
    const l2Rows = rows
      .filter((row) => row.parentCategoryId === l1.id && row.visible !== false)
      .sort(
        (a, b) =>
          a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
      );

    for (const l2 of l2Rows) {
      const inGroup = services.filter((service) =>
        service.categories?.some(({ id }) => id === l2.id),
      );
      if (inGroup.length > 0) {
        groups.push({ group: { id: l2.id, name: l2.name }, services: inGroup });
      }
    }
  }

  return groups;
}
